import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "currentOrganisationId";
const USER_STORAGE_KEY = "currentOrganisationUserId";

interface UseCurrentOrganisationReturn {
  currentOrganisationId?: string;
  setCurrentOrganisationId: (orgId: string) => void;
  clearOrganisationId: () => void;
  isLoading: boolean;
}

const useCurrentOrganisation = (
  userHomeOrgId?: number,
  currentUserId?: string
): UseCurrentOrganisationReturn => {
  const [currentOrganisationId, setCurrentOrganisationIdState] = useState<
    string | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedOrgId = window.localStorage.getItem(STORAGE_KEY);
    const storedUserId = window.localStorage.getItem(USER_STORAGE_KEY);

    // While the token user id isn't yet resolved we can't safely validate
    // the stored id. Restore it optimistically and re-evaluate when a real
    // user id arrives — clearing here would wipe a same-user's stored org
    // every time the app reloads.
    if (!currentUserId) {
      if (storedOrgId) {
        setCurrentOrganisationIdState(storedOrgId);
      } else if (userHomeOrgId) {
        setCurrentOrganisationIdState(userHomeOrgId.toString());
      } else {
        setCurrentOrganisationIdState(undefined);
      }

      setIsLoading(false);
      return;
    }

    const storedBelongsToCurrentUser =
      storedOrgId && storedUserId && storedUserId === currentUserId;

    if (storedBelongsToCurrentUser) {
      setCurrentOrganisationIdState(storedOrgId);
    } else {
      if (storedOrgId) {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(USER_STORAGE_KEY);
      }

      if (userHomeOrgId) {
        setCurrentOrganisationIdState(userHomeOrgId.toString());
      } else {
        setCurrentOrganisationIdState(undefined);
      }
    }

    setIsLoading(false);
  }, [userHomeOrgId, currentUserId]);

  const setCurrentOrganisationId = useCallback(
    (orgId: string) => {
      if (!currentUserId) {
        return;
      }

      setCurrentOrganisationIdState(orgId);
      window.localStorage.setItem(STORAGE_KEY, orgId);
      window.localStorage.setItem(USER_STORAGE_KEY, currentUserId);
    },
    [currentUserId]
  );

  const clearOrganisationId = useCallback(() => {
    setCurrentOrganisationIdState(undefined);
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(USER_STORAGE_KEY);
  }, []);

  return {
    currentOrganisationId,
    setCurrentOrganisationId,
    clearOrganisationId,
    isLoading,
  };
};

export default useCurrentOrganisation;
